import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Building,
  Folder,
  Globe,
  List,
  Plus,
  Search,
  Sliders,
  Table,
  ExternalLink,
  Wand2,
} from 'lucide-react';
import PageHeader from '../../../components/PageHeader';
import AnswersWorkspace from '../components/AnswersWorkspace';
import { AnswersRagFilterPanel, AnswersRagScopeChips } from '../components/answersRagFilters';
import { useAnswersRagFilters } from '../hooks/useAnswersRagFilters';
import { useAnswersRagSession } from '../hooks/useAnswersRagSession';

/** Chat empty-state quick asks — card UI, morph + submit on click. */
const LANDING_STARTER_CARDS = [
  { title: 'Products available', query: 'What products are available?', meta: 'Quick ask', MetaIcon: Search },
  { title: 'Compare rates', query: 'Compare premium rates', meta: 'Quick ask', MetaIcon: Table },
  { title: 'Summarize objections', query: 'Summarize recent objections and responses', meta: 'Quick ask', MetaIcon: Wand2 },
];

const Answers = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [morph, setMorph] = useState(null);
  const [copiedId, setCopiedId] = useState(null);

  const searchRef = useRef(null);
  const searchBoxRef = useRef(null);
  const targetInputRef = useRef(null);
  const scrollRef = useRef(null);
  const landingBootstrapRef = useRef(false);
  const autoScrollMetaRef = useRef({ length: 0, lastId: null, wasLoading: false });

  const filters = useAnswersRagFilters();
  const rag = useAnswersRagSession(filters.buildFilterPayload);

  useEffect(() => {
    if (location.state?.reset == null) return;
    setInputValue('');
    setHasStarted(false);
    setMorph(null);
    setCopiedId(null);
    landingBootstrapRef.current = false;
    rag.resetSession();
    filters.setFiltersOpen(false);
    navigate(location.pathname, { replace: true, state: {} });
  }, [
    filters.setFiltersOpen,
    location.pathname,
    location.state?.reset,
    navigate,
    rag.resetSession,
  ]);

  useEffect(() => {
    if (landingBootstrapRef.current || !filters.filterOptionsLoaded) return;
    const { initialQuestion, openFilters, filterSection } = location.state || {};
    if (!initialQuestion && !openFilters) return;

    landingBootstrapRef.current = true;
    const returnTo = location.state?.returnTo;
    navigate(location.pathname, { replace: true, state: returnTo ? { returnTo } : {} });

    if (openFilters) {
      filters.openFiltersFromLanding(filterSection);
    }
    if (initialQuestion) {
      setHasStarted(true);
      rag.submitQuery(initialQuestion);
    }
  }, [
    filters.filterOptionsLoaded,
    filters.openFiltersFromLanding,
    location.pathname,
    location.state,
    navigate,
    rag.submitQuery,
  ]);

  useEffect(() => {
    const list = rag.conversation || [];
    const length = list.length;
    const lastId = length ? list[length - 1]?.id : null;
    const prev = autoScrollMetaRef.current;
    const hasNewTurn = length > prev.length;
    const activeTurnChanged = lastId && lastId !== prev.lastId;
    const loadingStarted = rag.loading && !prev.wasLoading;

    if (scrollRef.current && (hasNewTurn || activeTurnChanged || loadingStarted)) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }

    autoScrollMetaRef.current = { length, lastId, wasLoading: rag.loading };
  }, [rag.conversation, rag.loading]);

  useEffect(() => {
    if (hasStarted && morph?.stage === 'init') {
      let timer;
      const tryLocate = () => {
        if (targetInputRef.current) {
          const targetRect = targetInputRef.current.getBoundingClientRect();
          setMorph((prev) => ({
            ...prev,
            stage: 'animating',
            targetRect: {
              top: targetRect.top,
              left: targetRect.left,
              width: targetRect.width,
              height: targetRect.height,
            },
          }));
        } else {
          timer = setTimeout(tryLocate, 25);
        }
      };
      timer = setTimeout(tryLocate, 50);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [hasStarted, morph?.stage]);

  useEffect(() => {
    if (morph?.stage === 'animating') {
      const endTimer = setTimeout(() => {
        const query = morph.queryText;
        setMorph(null);
        rag.submitQuery(query);
      }, 760);
      return () => clearTimeout(endTimer);
    }
    return undefined;
  }, [morph?.stage, morph?.queryText, rag.submitQuery]);

  const handleInitiateSearch = (q, sourceElement) => {
    if (!q || rag.loading) return;
    const rect = sourceElement.getBoundingClientRect();
    setMorph({
      stage: 'init',
      queryText: q,
      initialRect: { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      targetRect: null,
    });
    setHasStarted(true);
    rag.setError('');
    setInputValue('');
  };

  const handleLandingSubmit = () => {
    if (!inputValue.trim() || !searchBoxRef.current) return;
    handleInitiateSearch(inputValue.trim(), searchBoxRef.current);
  };

  const handleCopyAnswer = (turn) => {
    const text = turn.response?.answer || turn.streamingAnswer || '';
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => {
        setCopiedId(turn.id);
        setTimeout(() => setCopiedId(null), 2000);
      })
      .catch(() => {});
  };

  const handleQuickAsk = (query) => {
    if (!query?.trim() || rag.loading || !searchBoxRef.current) return;
    handleInitiateSearch(query.trim(), searchBoxRef.current);
  };

  const handleStarterCard = ({ query }) => {
    if (query) handleQuickAsk(query);
  };

  const ghostStyles = morph ? {
    top: morph.stage === 'animating' && morph.targetRect ? morph.targetRect.top : morph.initialRect.top,
    left: morph.stage === 'animating' && morph.targetRect ? morph.targetRect.left : morph.initialRect.left,
    width: morph.stage === 'animating' && morph.targetRect ? morph.targetRect.width : morph.initialRect.width,
    height: morph.stage === 'animating' && morph.targetRect ? morph.targetRect.height : morph.initialRect.height,
    opacity: morph.stage === 'animating' ? 0.9 : 1,
    borderRadius: morph.stage === 'animating' ? '12px' : '16px',
  } : {};

  const filterPanel = (
    <AnswersRagFilterPanel
      drawerFiltersMatchText={filters.drawerFiltersMatchText}
      isOpen={filters.filtersOpen}
      onApplyFilters={filters.applyFilters}
      onClearDraftFilters={filters.clearDraftFilters}
      onClose={filters.onCloseFilters}
    >
      {filters.filterDrawer()}
    </AnswersRagFilterPanel>
  );

  if (!hasStarted && rag.conversation.length === 0) {
    return (
      <>
        <PageHeader title="Answers" />
        <div className="answers-landing-page">
          <div className="landing-container">
            <h1 className="landing-title">What are you looking for?</h1>
            <p className="landing-subtitle">
              Ask questions in natural language and get instant, cited answers from ingested documents. Built for insurance.
            </p>
            {/* <ul className="landing-features">
              <li>
                <span className="landing-feature-dot" aria-hidden="true" />
                Search across ingested documents with insurance-specific filters
              </li>
              <li>
                <span className="landing-feature-dot" aria-hidden="true" />
                Answers include source citations and links to source documents
              </li>
            </ul> */}

            <div className="landing-search-wrapper">
              {/* <div className="lsw-top-bar">
                <button type="button" className="lsw-pill-btn" onClick={() => filters.openFiltersFromLanding('type')}>
                  <Folder size={14} />
                  Choose filing type
                </button>
                <button type="button" className="lsw-pill-btn" onClick={() => filters.openFiltersFromLanding('state')}>
                  <Briefcase size={14} />
                  Set state jurisdiction
                </button>
              </div> */}

              <div ref={searchBoxRef} className="main-search-box">
                <textarea
                  ref={searchRef}
                  className="main-search-textarea"
                  rows={4}
                  placeholder="Ask Answers anything about insurance compliance..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleLandingSubmit();
                    }
                  }}
                />
                <div className="main-search-footer">
                  <div className="msf-left">
                    <button
                      type="button"
                      className="msf-action"
                      onClick={() => filters.openFiltersFromLanding()}
                    >
                      <Sliders size={14} aria-hidden="true" />
                      Configure scope
                    </button>
                    <AnswersRagScopeChips chips={filters.activeScopeChips} />
                  </div>
                  <button
                    type="button"
                    className="msf-btn-primary"
                    onClick={handleLandingSubmit}
                    disabled={!inputValue.trim()}
                  >
                    Ask Answers
                  </button>
                </div>
              </div>

              <section className="recommended-section">
                <div className="rs-header">
                  <span className="rs-title">Quick asks</span>
                </div>
                <div className="rs-grid">
                  {LANDING_STARTER_CARDS.map(({ title, query, meta, MetaIcon }) => (
                    <button
                      key={title}
                      type="button"
                      className="rs-card"
                      onClick={() => handleStarterCard({ query })}
                      disabled={rag.loading}
                    >
                      <span className="rs-card-title">{title}</span>
                      <div className="rs-card-footer">
                        <span className="rs-card-meta">
                          <MetaIcon size={10} />
                          {meta}
                        </span>
                        <ExternalLink size={12} className="rs-card-action-icon" aria-hidden="true" />
                      </div>
                    </button>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
        {filterPanel}
      </>
    );
  }

  return (
    <>
      {morph && (
        <div className={`morph-ghost ${morph.stage === 'animating' ? 'animating' : ''}`} style={ghostStyles}>
          <div className="morph-ghost-text">{morph.queryText}</div>
        </div>
      )}

      <AnswersWorkspace
        activeFiltersCount={filters.activeFiltersCount}
        activeScopeChips={filters.activeScopeChips}
        conversation={rag.conversation}
        copiedId={copiedId}
        error={rag.error}
        filtersMatchText={filters.filtersMatchText}
        handleCitationClick={rag.handleCitationClick}
        handlePdfLoadError={rag.handlePdfLoadError}
        handlePdfLoadSuccess={rag.handlePdfLoadSuccess}
        inputText={rag.newMessage}
        isQuerying={rag.loading}
        morph={morph}
        onClearFilters={filters.clearFilters}
        onCopyAnswer={handleCopyAnswer}
        filtersOpen={filters.filtersOpen}
        onToggleFilters={() => filters.setFiltersOpen((open) => !open)}
        onSendFollowUp={() => rag.submitQuery()}
        onSetInputText={rag.setNewMessage}
        onSetPdfLoadError={rag.setPdfLoadError}
        onSetSelectedSource={rag.setSelectedSource}
        onSuggestedQuestion={rag.submitQuery}
        pdfLoadError={rag.pdfLoadError}
        referencesForTurn={rag.referencesForTurn}
        scrollRef={scrollRef}
        selectedPdfUrl={rag.selectedPdfUrl}
        selectedSource={rag.selectedSource}
        targetInputRef={targetInputRef}
        updateTurn={rag.updateTurn}
      />
      {filterPanel}
    </>
  );
};

export default Answers;
